import React from 'react';
import { Link } from 'react-router-dom';
import './TermsConditionsPage.css';

const termsSections = [
  ['about-teamward', '1. About Teamward'],
  ['listing-information', '2. Listings and Information'],
  ['third-party-providers', '3. Third-Party Providers'],
  ['recommendations', '4. Recommendations'],
  ['planning-services', '5. Planning Services'],
  ['vendor-costs', '6. Vendor Costs'],
  ['user-responsibilities', '7. User Responsibilities'],
  ['partner-listings', '8. Partner Listings'],
  ['intellectual-property', '9. Intellectual Property'],
  ['acceptable-use', '10. Acceptable Use'],
  ['external-links', '11. External Links'],
  ['warranties', '12. Disclaimer of Warranties'],
  ['liability', '13. Limitation of Liability'],
  ['privacy', '14. Privacy'],
  ['changes', '15. Changes to These Terms'],
  ['governing-law', '16. Governing Law'],
  ['contact', '17. Contact'],
];

const TermsConditionsPage = () => {
  const scrollToTermsSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <main className="terms-page">
      <section className="terms-hero">
        <p className="terms-eyebrow">Teamward Legal</p>

        <h1>Terms of Use</h1>

        <p className="terms-intro">
          These Terms of Use govern access to and use of Teamward, including
          its listings, planning tools, recommendations, forms, and related
          services.
        </p>

        <p className="terms-effective-date">
          Effective date: August 21, 2026
        </p>
      </section>

      <div className="terms-layout">
        <aside className="terms-sidebar">
          <p className="terms-sidebar-title">On this page</p>

          <nav aria-label="Terms of Use sections">
            {termsSections.map(([sectionId, label]) => (
              <button
                key={sectionId}
                type="button"
                onClick={() => scrollToTermsSection(sectionId)}
              >
                {label}
              </button>
            ))}
          </nav>
        </aside>

        <article className="terms-content">
          <div className="terms-notice">
            <strong>Please read these Terms carefully.</strong>

            <p>
              By accessing or using Teamward, you agree to these Terms. If you
              do not agree, please do not use the website.
            </p>
          </div>

          <section id="about-teamward" className="terms-section">
            <span className="terms-number">01</span>
            <h2>About Teamward</h2>

            <p>
              Teamward helps businesses discover and plan team outings,
              trainings, speakers, retreats, volunteer events, and other team
              experiences.
            </p>

            <p>The website may provide:</p>

            <ul>
              <li>Curated business and experience listings</li>
              <li>Search and filtering tools</li>
              <li>Automated or instant recommendations</li>
              <li>Personalized recommendations</li>
              <li>Optional event-planning and coordination assistance</li>
            </ul>

            <p>
              Website content is provided for general informational and
              planning purposes.
            </p>
          </section>

          <section id="listing-information" className="terms-section">
            <span className="terms-number">02</span>
            <h2>Listings and Website Information</h2>

            <p>
              Teamward works to provide useful and accurate information.
              However, business details may change without notice, including:
            </p>

            <ul>
              <li>Pricing</li>
              <li>Availability</li>
              <li>Hours</li>
              <li>Group-size limits</li>
              <li>Event duration</li>
              <li>Policies</li>
              <li>Services offered</li>
              <li>Locations</li>
              <li>Images and descriptions</li>
            </ul>

            <p>
              Teamward does not guarantee that all information on the website
              is complete, current, or error-free.
            </p>

            <p>
              Users should confirm important details directly with the
              applicable venue, vendor, speaker, trainer, nonprofit, or other
              service provider before making a purchase or commitment.
            </p>
          </section>

          <section id="third-party-providers" className="terms-section">
            <span className="terms-number">03</span>
            <h2>Third-Party Providers</h2>

            <p>
              Most experiences displayed on Teamward are offered and operated
              by independent third parties.
            </p>

            <p>
              Unless expressly stated otherwise, Teamward does not own,
              operate, supervise, or control these businesses or their
              employees.
            </p>

            <p>Teamward is not responsible for a third party’s:</p>

            <ul>
              <li>Services or performance</li>
              <li>Availability or pricing</li>
              <li>Safety practices</li>
              <li>Insurance or licensing</li>
              <li>Cancellation or refund policies</li>
              <li>Acts, omissions, representations, or conduct</li>
            </ul>

            <p>
              Any agreement or transaction between a user and a third-party
              provider is between those parties.
            </p>
          </section>

          <section id="recommendations" className="terms-section">
            <span className="terms-number">04</span>
            <h2>Recommendations</h2>

            <p>
              Recommendations may be based on information provided by the user,
              including location, group size, budget, setting, goals, and
              timing.
            </p>

            <p>
              Instant matches are generated from the information available
              within the Teamward directory. Personalized recommendations may
              be reviewed or prepared by a real person.
            </p>

            <p>
              Recommendations are intended to provide a useful starting point.
              They are not guarantees that an option will be available,
              appropriate, safe, or satisfactory for every group.
            </p>

            <p>
              Users remain responsible for evaluating each option and
              confirming that it meets their organization’s requirements.
            </p>
          </section>

          <section id="planning-services" className="terms-section">
            <span className="terms-number">05</span>
            <h2>Planning and Coordination Services</h2>

            <p>
              Submitting a request for planning assistance does not
              automatically create a paid service agreement.
            </p>

            <p>
              Any paid event-planning or coordination services will be governed
              by a separate written proposal, agreement, or statement of work.
              That document may address:
            </p>

            <ul>
              <li>Scope of services</li>
              <li>Planning fees</li>
              <li>Payment schedule</li>
              <li>Vendor expenses</li>
              <li>Deposits</li>
              <li>Cancellations</li>
              <li>Rescheduling</li>
              <li>On-site support</li>
              <li>Client responsibilities</li>
              <li>Additional or out-of-scope work</li>
            </ul>

            <p>
              Teamward will not make paid reservations, enter into vendor
              contracts, or incur expenses on a client’s behalf without
              appropriate authorization.
            </p>
          </section>

          <section id="vendor-costs" className="terms-section">
            <span className="terms-number">06</span>
            <h2>Vendor Costs and Payments</h2>

            <p>
              Unless a separate written agreement states otherwise, venue
              charges, catering, transportation, entertainment, lodging,
              activity fees, equipment rentals, gratuities, taxes, and other
              vendor expenses are paid separately from Teamward’s planning
              fees.
            </p>

            <p>
              Third-party payments may be subject to the provider’s own terms,
              deposit requirements, cancellation policies, and refund rules.
            </p>
          </section>

          <section id="user-responsibilities" className="terms-section">
            <span className="terms-number">07</span>
            <h2>User Responsibilities</h2>

            <p>
              When using Teamward, you agree to provide information that is
              accurate to the best of your knowledge.
            </p>

            <p>You are responsible for:</p>

            <ul>
              <li>Confirming final pricing and availability</li>
              <li>Reviewing vendor contracts and policies</li>
              <li>Communicating accessibility or accommodation needs</li>
              <li>Disclosing relevant age restrictions or safety concerns</li>
              <li>Obtaining internal company approvals</li>
              <li>
                Ensuring that an experience is suitable for your participants
              </li>
              <li>Complying with applicable laws and workplace policies</li>
            </ul>
          </section>

          <section id="partner-listings" className="terms-section">
            <span className="terms-number">08</span>
            <h2>Paid, Featured, or Partner Listings</h2>

            <p>
              Teamward may receive listing fees, sponsorship payments,
              referral compensation, commissions, or other compensation from
              certain businesses or partners.
            </p>

            <p>
              Compensation may affect whether or how a business is presented.
              Paid or sponsored placements will be identified when required.
            </p>

            <p>
              Teamward may also include businesses that have not paid to be
              listed.
            </p>

            <p>
              A paid relationship does not guarantee that a provider will be
              recommended for every user or planning request.
            </p>
          </section>

          <section id="intellectual-property" className="terms-section">
            <span className="terms-number">09</span>
            <h2>Intellectual Property</h2>

            <p>
              The website and its original content, design, branding, graphics,
              written materials, software, and organization are owned by or
              licensed to Teamward and are protected by applicable
              intellectual-property laws.
            </p>

            <p>
              The Teamward name, logos, taglines, and related branding are
              owned by or licensed to Teamward and may be protected by
              trademark and other intellectual-property laws.
            </p>

            <p>
              You may not copy, reproduce, distribute, modify, sell, scrape,
              republish, or commercially exploit website content without prior
              written permission, except as permitted by law.
            </p>

            <p>
              Third-party names, logos, photographs, and trademarks remain the
              property of their respective owners.
            </p>
          </section>

          <section id="acceptable-use" className="terms-section">
            <span className="terms-number">10</span>
            <h2>Acceptable Use</h2>

            <p>You may not use the website to:</p>

            <ul>
              <li>Violate any law</li>
              <li>Submit false or misleading information</li>
              <li>Interfere with website security or operation</li>
              <li>Attempt unauthorized access to website systems</li>
              <li>Introduce malware or harmful code</li>
              <li>
                Scrape or collect website data through automated means without
                permission
              </li>
              <li>Impersonate another person or organization</li>
              <li>
                Use Teamward content to create a competing directory or
                database
              </li>
            </ul>

            <p>
              Teamward may restrict or terminate access for misuse of the
              website.
            </p>
          </section>

          <section id="external-links" className="terms-section">
            <span className="terms-number">11</span>
            <h2>External Links</h2>

            <p>
              The website may link to third-party websites, forms, booking
              systems, or other online services.
            </p>

            <p>
              Teamward does not control those services and is not responsible
              for their content, accessibility, security, privacy practices, or
              availability.
            </p>

            <p>
              Your use of third-party services is governed by their own terms
              and policies.
            </p>
          </section>

          <section id="warranties" className="terms-section">
            <span className="terms-number">12</span>
            <h2>Disclaimer of Warranties</h2>

            <p>
              The website and its content are provided on an “as is” and “as
              available” basis.
            </p>

            <p>
              To the fullest extent permitted by law, Teamward makes no
              warranties, express or implied, regarding the website, listings,
              recommendations, availability, accuracy, reliability,
              merchantability, fitness for a particular purpose, or
              non-infringement.
            </p>
          </section>

          <section id="liability" className="terms-section">
            <span className="terms-number">13</span>
            <h2>Limitation of Liability</h2>

            <p>
              To the fullest extent permitted by law, Teamward will not be
              liable for indirect, incidental, special, consequential, or
              punitive damages arising from:
            </p>

            <ul>
              <li>Use of or inability to use the website</li>
              <li>Reliance on website information</li>
              <li>Third-party providers or services</li>
              <li>
                Cancellations, delays, injuries, property damage, or losses
                connected with an experience
              </li>
              <li>
                Unauthorized access to information outside Teamward’s
                reasonable control
              </li>
            </ul>

            <p>
              Nothing in these Terms excludes liability that cannot legally be
              excluded.
            </p>
          </section>

          <section id="privacy" className="terms-section">
            <span className="terms-number">14</span>
            <h2>Privacy</h2>

            <p>
              Information submitted through Teamward may include names, email
              addresses, company information, event details, and other
              information voluntarily provided by users.
            </p>

            <p>
              Information will be used to respond to inquiries, provide
              recommendations, deliver planning services, improve the website,
              and communicate about requested services.
            </p>

            <p>
  For more information about how Teamward collects, uses, and
  shares information, please review the{' '}
  <Link to="/privacy">Teamward Privacy Policy</Link>.
</p>
          </section>

          <section id="changes" className="terms-section">
            <span className="terms-number">15</span>
            <h2>Changes to These Terms</h2>

            <p>Teamward may update these Terms periodically.</p>

            <p>
              Changes will be posted on this page with a revised effective
              date. Continued use of the website after changes are posted
              constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section id="governing-law" className="terms-section">
            <span className="terms-number">16</span>
            <h2>Governing Law</h2>

            <p>
              These Terms are governed by the laws of the State of Wyoming,
              without regard to conflict-of-law principles.
            </p>

            <p>
              Any dispute relating to the website or these Terms will be
              handled in an appropriate court located in Wyoming, unless
              applicable law requires otherwise.
            </p>
          </section>

          <section id="contact" className="terms-section terms-contact-section">
            <span className="terms-number">17</span>
            <h2>Contact</h2>

            <p>Questions about these Terms may be sent to:</p>

            <div className="terms-contact-card">
              <strong>Teamward</strong>

              <a href="mailto:mkuhl@teamward.ai">mkuhl@teamward.ai</a>

              <a
                href="https://teamward.ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                teamward.ai
              </a>
            </div>
          </section>

          <div className="terms-bottom-navigation">
            <Link to="/">Return Home</Link>
            <Link to="/about">About Teamward</Link>
          </div>

  
        </article>
      </div>
    </main>
  );
};

export default TermsConditionsPage;
